import { Shield, FileText, User, Clock, Link2, Globe } from "lucide-react"
import Header from "../Header"

export default function PrivacyPolicy() {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-[#f5f3fa] py-12 px-4 font-poppins tracking-wide leading-6 text-sm">
        <div className="max-w-4xl mx-auto">
          <header className="text-center mb-10">
            <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
            <p className="text-gray-600">Last Updated: June 7, 2025</p>
          </header>

          <div className="space-y-6">
            {/* Introduction Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <Shield className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">Introduction</h2>
                  <p className="text-gray-500 text-sm">Our commitment to your privacy</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>
                  Agentic Marketing Solutions and its affiliates and subsidiaries ("Agentic," "we," "us," or "our")
                  respect your privacy. This Privacy Notice ("Notice") describes the processing of Personal Information
                  (defined below) that is provided, collected, or disclosed while providing our products or services to
                  you ("Services") and on the websites (including https://siAi.live), applications, and online platforms
                  that link to this Notice (collectively, "Site"), including when you apply for a job with us. It also
                  describes rights you may have under applicable laws. Please read this Notice carefully to understand our
                  policies and practices regarding your Personal Information and how we will treat it. To access this
                  Notice in an alternative downloadable format, please click here.
                </p>
                <p>
                  We may provide you with a different privacy policy or notice in certain specific situations, in which
                  case that privacy notice or policy will apply to the Personal Information collected or processed in that
                  specific situation, rather than this one.
                </p>
                <p>
                  If you provide us with Personal Information related to anyone other than yourself, please note that you
                  are responsible for complying with all privacy and data protection laws prior to providing that
                  information to Agentic (including obtaining consent, if required).
                </p>
                <p>
                  This Notice applies only to instances where Agentic acts as a data controller. For purposes of data
                  protection laws, Agentic is "data controller" (or similar term under applicable laws) and is generally
                  responsible for, and controls, the processing of Personal Information about our site visitors collected
                  through those visitors' use of our Services.
                </p>
                <p>
                  For some of our Services, we may process Personal Information as a service provider at the direction of
                  third-party data controllers such as our business-to-business customers or other organizations. In those
                  circumstances, we act as a "data processor." Where we process your Personal Information as a data
                  processor, you should consult the data controller's applicable privacy notice or policy or reach out to
                  the data controller for information regarding how they process your Personal Information. When
                  processing Personal Information as a data processor, where required by law, we may sign a data
                  processing agreement with the data controller, through which we will define our responsibilities
                  regarding processing activities.
                </p>
              </div>
            </section>

            {/* Personal Information We Collect Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <FileText className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">Personal Information We Collect</h2>
                  <p className="text-gray-500 text-sm">Types of data we gather</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>
                  We collect several categories of Personal Information from and about users of our Site and Services.
                  "Personal Information" means information that uniquely identifies, relates to, describes, or is
                  reasonably capable of being associated with or linked to you. The categories of Personal Information we
                  collect may include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Contact Information</strong> – If you submit an inquiry, register for an account, or provide
                    information on or through our Site or Services, we may collect your contact information including your
                    name, mailing address, email address, and phone number.
                  </li>
                  <li>
                    <strong>Commercial Information</strong> – If you submit an inquiry, or provide information on our
                    Site, we may collect commercial information including information about your purchases, subscriptions,
                    and Services you have shown interest in.
                  </li>
                  <li>
                    <strong>Usage Information</strong> – When you use our Site, we may automatically record information,
                    including your Internet Protocol address (IP Address), geolocation of your device, browser type,
                    referring URLs (e.g., the website you visited before coming to our Site), domain names associated with
                    your internet service provider, and any other information regarding your interaction with our Site.
                  </li>
                  <li>
                    <strong>Employment Information</strong> – If you apply for employment with us, we will collect
                    Personal Information related to your potential employment, including your education and employment
                    history, address and contact information, demographic information, and any other information included
                    in your resume or application.
                  </li>
                  <li>
                    <strong>Communication Information</strong> – We may collect Personal Information contained within your
                    communications with us via email, chat functionality, social media, telephone, or otherwise, and in
                    certain cases we may use third-party service providers to do so. Where permitted by applicable law, we
                    may collect and maintain records of calls and chats with our agents, representatives, or employees via
                    message, chat, post, or similar functionality.
                  </li>
                  <li>
                    <strong>Financial Information</strong> – If you use our Services, we may collect financial information
                    such as credit card details via our third-party payment processor to facilitate online payments.
                  </li>
                </ul>
              </div>
            </section>

            {/* How We Collect Personal Information Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <FileText className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">How We Collect Personal Information</h2>
                  <p className="text-gray-500 text-sm">Sources of your information</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Directly From You</strong> – We collect Personal Information that you provide to us directly,
                    for example, if you choose to contact us, request information from us, sign up to receive updates, or
                    otherwise utilize our Site or Services.
                  </li>
                  <li>
                    <strong>From Third Parties</strong> – We may collect Personal Information from third parties,
                    including but not limited to business partners, advertising networks, social networks, data analytics
                    providers, mobile device providers, Internet or mobile service providers, recruiters and job
                    application portals, and background check providers.
                  </li>
                  <li>
                    <strong>Through Online Tracking Technologies</strong> – We use cookies and similar technologies to
                    collect Personal Information automatically as you navigate our Site. For additional information
                    regarding our use of these technologies, see the Cookies and Tracking Technologies section below.
                  </li>
                </ul>
              </div>
            </section>

            {/* How We Use Personal Information Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <User className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">How We Use Personal Information</h2>
                  <p className="text-gray-500 text-sm">Purposes for data collection</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>To the extent permitted by applicable law, we use Personal Information:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    To provide and personalize our Site and Services, such as processing or fulfilling orders and
                    transactions, providing and personalizing our Services, processing payments, providing customer
                    service, maintaining or servicing accounts, providing financing, verifying customer information,
                    creating and maintaining business records, verifying eligibility, and undertaking or providing similar
                    services.
                  </li>
                  <li>
                    To optimize, improve, and maintain our Services, including understanding how users interact with our
                    Services, gauging user interest in certain Services or Site functionality, and troubleshooting
                    problems.
                  </li>
                  <li>
                    For internal research and development, such as testing, verifying, and improving the quality of our
                    Services or developing new ones.
                  </li>
                  <li>
                    For marketing and advertising, including using your information to send you messages, notices,
                    newsletters, surveys, promotions, or event invitations about our own or third parties' goods and
                    services that may be of interest to you. We also use Personal Information to conduct interest-based
                    advertising as discussed in the Cookies and Other Tracking Technologies section below. You can also
                    unsubscribe from any marketing emails or text messages that we may send you by following the
                    instructions included in the email or text correspondence or emailing us at legal@siai.live.
                  </li>
                  <li>
                    For communicating with you, such as responding to your questions and comments or notifying you of
                    changes to our Site or Services.
                  </li>
                  <li>
                    For legal, security, or safety reasons, such as protecting our and our users' safety, property, or
                    rights; complying with legal requirements; enforcing our terms, conditions, and policies; detecting,
                    preventing, and responding to security incidents; and protecting against malicious, deceptive,
                    fraudulent, or illegal activity.
                  </li>
                  <li>
                    As part of a corporate transaction, such as in connection with the sale of part or all of our assets
                    or business, the acquisition of part or all of another business or another business' assets, or
                    another corporate transaction, including bankruptcy.
                  </li>
                  <li>
                    To fulfill any other purpose for which you provide it, including purposes described when you provide
                    the information or give your consent.
                  </li>
                </ul>
                <p>
                  If you are applying for employment with us, we also use Personal Information to process your job
                  application, to verify the information you have provided in your application, conduct interviews,
                  perform background and reference checks, to communicate with you and answer your questions, to confirm
                  your eligibility for employment, and improve our recruiting processes. We may also save your Personal
                  Information for future employment opportunities with us.
                </p>
              </div>
            </section>

            {/* How We Disclose Personal Information Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <User className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">How We Disclose Personal Information</h2>
                  <p className="text-gray-500 text-sm">Sharing your information</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>We may disclose your Personal Information with your consent or in the following circumstances:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Employees and Other Personnel</strong> – We may share Personal Information with our employees
                    and personnel (such as contractors) who have a need to know the information for our business purposes.
                  </li>
                  <li>
                    <strong>Affiliates and Subsidiaries</strong> – We may share Personal Information within our family of
                    companies for their and our business and marketing purposes, including providing you with information
                    about the Services we think may be of interest to you.
                  </li>
                  <li>
                    <strong>Service Providers</strong> – We disclose your Personal Information with the service providers
                    that we use to support our business, including but not limited to, data analytics providers, website
                    hosting providers, and other technology providers. If you are applying for a job with us, this may
                    include service providers such as background check providers and human resource providers.
                  </li>
                  <li>
                    <strong>Business Partners</strong> – We may disclose Personal Information with trusted business
                    partners. For example, we may disclose your Personal Information with a company whose products or
                    services we think may be of interest to you or who we co-sponsor a promotion or service with.
                  </li>
                  <li>
                    <strong>Legal Obligation or Safety Reasons</strong> – We may disclose Personal Information to a third
                    party when we have a good faith belief that such disclosure of Personal Information is reasonably
                    necessary to (a) satisfy or comply with any requirement of law, regulation, legal process, or
                    enforceable governmental request, (b) enforce or investigate a potential violation of any agreement
                    you have with us, (c) detect, prevent, or otherwise respond to fraud, security or technical concerns,
                    (d) support auditing and compliance functions, or (e) protect the rights, property, or safety of
                    Agentic, its employees and clients, or the public against harm.
                  </li>
                  <li>
                    <strong>Merger or Change of Control</strong> – We may disclose Personal Information to third parties
                    as necessary if we are involved in a merger, acquisition, or any other transaction involving a change
                    of control in our business, including but not limited to, a bankruptcy or similar proceeding. Where
                    legally required, we will give you notice prior to such disclosure.
                  </li>
                  <li>
                    <strong>Other</strong> – We may disclose Personal Information to third parties when explicitly
                    requested by or consented to by you, or for the purposes for which you disclosed the Personal
                    Information to us as indicated at the time and point of the disclosure (or as was obvious at the time
                    and point of disclosure).
                  </li>
                </ul>
              </div>
            </section>

            {/* Cookies and Other Tracking Technologies Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <FileText className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">Cookies and Other Tracking Technologies</h2>
                  <p className="text-gray-500 text-sm">How we use tracking technologies</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>
                  We and our service providers may use cookies and similar technologies to collect usage and browser
                  information about how you use our Site. The technologies we use for this automatic data collection may
                  include cookies and web beacons that permit us to verify system and server integrity and generate
                  statistics around the popularity of certain content. We process the information collected through such
                  technologies, which may include or be combined with Personal Information, to help operate certain
                  features of our Site, to enhance your experience through personalization, and to help us better
                  understand the features of our Site that you and other users are most interested in.
                </p>
                <p>
                  <strong>Website Delivery and Appearance</strong> – We may use third-party providers to enable certain
                  customer interaction opportunities, content delivery (like audio or video), or other service
                  capabilities. Examples include, but are not limited to, the following functionality:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Content Delivery</strong>: We partner with service providers to host our Site and deliver
                    specific content in support of our Services. For example, we use Google Fonts to serve fonts on our
                    Site. Please see Google's Privacy Notice for more information on how that feature operates.
                  </li>
                </ul>
                <p>
                  <strong>Website Analytics and Session Replay</strong> – We may use analytics and session replay
                  services, that use cookies and other technologies that collect your Personal Information, to assist us
                  with analyzing our Site traffic and site usage to optimize, maintain, and secure our Site and inform
                  subsequent business decisions (including, e.g., advertising). These include, but are not limited to, the
                  following third-party services:
                </p>
                <p>
                  <strong>Selling or Sharing Personal Information</strong> – While we do not sell Personal Information in
                  exchange for monetary consideration, we do share Personal Information for other benefits that could be
                  deemed a "sale" or "sharing" under various data protection laws, because they are sometimes broadly
                  defined to include activities such as allowing third parties to receive certain information, such as
                  cookies, IP address, and/or browsing behavior. It is in this context that we may provide data analytics
                  providers, social networks, or video sharing platforms with Personal Information such as your IP
                  address, device information, Internet and other electronic network activity information, and geolocation
                  information.
                </p>
                <p>
                  <strong>Cookie Choices</strong> – To manage your preferences with respect to these technologies, you can
                  visit our preferences page or customize your browser settings to refuse all or some browser cookies, or
                  to alert you when websites set or access cookies. If you disable certain cookies, please note that some
                  parts of our Site may not function properly. These settings may be lost and require reconfiguration if
                  you delete your cookies.
                </p>
              </div>
            </section>

            {/* De-Identified/Aggregate Information Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <FileText className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">De-Identified/Aggregate Information</h2>
                  <p className="text-gray-500 text-sm">Non-personal information usage</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>
                  We may collect or create information that is de-identified or aggregate information that does not
                  identify a natural person ("De-Identified Information"). Agentic is the sole and exclusive owner of
                  De-Identified Information and may use or disclose such information for any legal business purpose. For
                  example, Agentic may create aggregate information about applicants to learn about the success of its
                  advertising efforts. Agentic will maintain and use De-Identified Information only in de-identified form
                  and will not to attempt to reidentify the information, except that Agentic may attempt to re-identify
                  the information solely for the purpose of determining whether its de-identification processes satisfy
                  the requirements of applicable law.
                </p>
              </div>
            </section>

            {/* How Long We Keep Your Personal Information Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <Clock className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">How Long We Keep Your Personal Information</h2>
                  <p className="text-gray-500 text-sm">Data retention practices</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>
                  We retain your information for as long as needed: (i) to conduct business with you; (ii) fulfill the
                  purposes outlined in this policy; and (iii) to comply with our legal obligations, resolve disputes, and
                  enforce any agreements.
                </p>
                <p>
                  Criteria we will use to determine how long to retain your Personal Information include the nature and
                  length of our business relationship with you; our legal rights, obligations, and retention requirements;
                  and if we have an ongoing business purpose for retaining your Personal Information, such as
                  communicating with you about ongoing or prospective Services you requested.
                </p>
              </div>
            </section>

            {/* Links to Third-Party Websites Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <Link2 className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">Links to Third-Party Websites</h2>
                  <p className="text-gray-500 text-sm">External site policies</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>
                  We are not responsible for the practices employed by any websites or services linked to or from our
                  Site, including the information or content contained within them. We encourage you to investigate and
                  ask questions before disclosing Personal Information to third parties, since any Personal Information
                  disclosed will be handled in accordance with the applicable third party's privacy policy.
                </p>
                <p>
                  In some cases, we may offer links to social media platforms that enable you to easily connect with us or
                  share information on social media. Any content you post via these social media pages is subject to the
                  Terms of Use and Privacy Policies for those platforms.
                </p>
              </div>
            </section>

            {/* International Use and Cross-Border Data Transfers Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <Globe className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">International Use and Cross-Border Data Transfers</h2>
                  <p className="text-gray-500 text-sm">Global data processing</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>
                  If you are visiting our Site from outside of the United States, please note that our Site is hosted in
                  the United States. Where permitted by applicable law, we may transfer the personal data we collect about
                  you to the United States and other jurisdictions that may not be deemed to provide the same level of
                  data protection as your home country, as necessary for the purposes set out in this Notice.
                </p>
              </div>
            </section>

            {/* How We Protect Personal Information Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <Shield className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">How We Protect Personal Information</h2>
                  <p className="text-gray-500 text-sm">Security measures</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>
                  We have implemented commercially reasonable measures designed to secure your Personal Information from
                  accidental loss and from unauthorized access, use, alteration, and disclosure. Unfortunately, the
                  transmission of information via the internet is not completely secure. Despite these efforts to store
                  Personal Information in a secure environment, we cannot guarantee the security of Personal Information
                  during its transmission or its storage on our systems.
                </p>
              </div>
            </section>

            {/* Exercising Your Privacy Rights Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <User className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">Exercising Your Privacy Rights</h2>
                  <p className="text-gray-500 text-sm">Managing your data</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>
                  Depending on where you live, you may have the following rights with respect to your Personal Information
                  under applicable data protection laws:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Access</strong> – The right to request access to and obtain a copy of any Personal Information
                    we may have about you.
                  </li>
                  <li>
                    <strong>Deletion</strong> – The right to delete your Personal Information that we have collected or
                    obtained, subject to certain exceptions.
                  </li>
                  <li>
                    <strong>Correction</strong> – The right to request that we correct any inaccuracies in your Personal
                    Information, subject to certain exceptions.
                  </li>
                  <li>
                    <strong>Opt Out of Certain Processing</strong> – The right to: (a) opt out of the processing of your
                    Personal Information for purposes of targeted advertising, (b) opt out of the sale of your Personal
                    Information; (c) limit the use of your sensitive Personal Information (if applicable), and (d) opt out
                    of profiling in furtherance of decisions that produce legal or similarly significant effects.
                  </li>
                  <li>
                    <strong>Objection/Restriction of Processing</strong> – The right to object or restrict us from
                    processing your Personal Information in certain circumstances.
                  </li>
                  <li>
                    <strong>Withdraw Consent</strong> – The right to withdraw your consent where we are relying on your
                    consent to process your Personal Information.
                  </li>
                  <li>
                    <strong>Automated Decision-Making</strong> – The right to know when you are subject to automated
                    decision-making, the Personal Information used to render the decision, the principal factors and
                    parameters involved in the decision, and human review or correction of the decision (or its underlying
                    data, where appropriate). You may also have the right to consent or opt out of this automated
                    decision-making, depending on your location.
                  </li>
                  <li>
                    <strong>Lodge a Complaint</strong> – The right to lodge a complaint with a supervisory authority or
                    other regulatory agency if you believe we have violated any of the rights afforded to you under
                    applicable data protection laws. We encourage you to first reach out to us so we have an opportunity
                    to address your concerns directly before you do so.
                  </li>
                </ul>
                <p>
                  To exercise any of the privacy rights afforded to you under applicable data protection laws, e-mail us
                  at legal@siai.live. We do not at this time recognize do-not-track browser or device signals.
                </p>
                <p>
                  You will not be discriminated against in any way by virtue of your exercise of the rights listed in this
                  Notice. However, should you withdraw your consent or object to processing of your Personal Information,
                  or if you choose not to provide certain Personal Information, we may be unable to provide some, or all,
                  of our Services to you.
                </p>
                <p>
                  Only you, or an authorized agent that you authorize to act on your behalf, may make a request related to
                  your Personal Information. We must verify your identity before fulfilling your requests, and if we
                  cannot verify your identity, we may request additional information from you. If you are an authorized
                  agent making a request on behalf of another person, we will also need to verify your identity, which may
                  require proof of your written authorization or evidence of a power of attorney. We endeavor to respond
                  to requests within the time period required by applicable law. If we require more time, we will inform
                  you of the reason and extension period in writing.
                </p>
                <p>
                  We do not charge a fee to process or respond to your requests unless they are excessive or repetitive.
                  If we determine that a request warrants a fee, we will tell you why we made that decision and provide
                  you with a cost estimate before completing your request. We may deny certain requests, or only fulfill
                  some in part, as permitted or required by law. If you are not satisfied with the resolution of your
                  request and you are afforded a right to appeal such decision, you will be notified of our appeal process
                  in our response to your request.
                </p>
              </div>
            </section>

            {/* Children's Privacy Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <User className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">Children's Privacy</h2>
                  <p className="text-gray-500 text-sm">Protection for minors</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>
                  We do not knowingly collect or solicit any Personal Information from children, as defined under
                  applicable law, without verified written parental consent, and we have no actual knowledge of selling
                  such Personal Information of minors under 16 years of age. If we learn that we have collected Personal
                  Information from a child, we will promptly take steps to delete that information. If you believe we
                  might have any information from or about a child, please contact us at legal@siai.live.
                </p>
              </div>
            </section>

            {/* California Residents Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <FileText className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">California Residents</h2>
                  <p className="text-gray-500 text-sm">California-specific rights</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>
                  <strong>California Shine the Light</strong> – Under California Civil Code Section 1798.83, individual
                  customers who reside in California and who have an existing business relationship with us may request
                  information about our disclosure of certain categories of Personal Information to third parties for the
                  third parties' direct marketing purposes, if any. To make such a request, please contact us at
                  legal@siai.live. Please be aware that not all information sharing is covered by these California privacy
                  rights requirements and only information on covered sharing will be included in our response. This
                  request may be made no more than once per calendar year.
                </p>
              </div>
            </section>

            {/* Changes to this Notice Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <FileText className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">Changes to this Notice</h2>
                  <p className="text-gray-500 text-sm">Policy updates</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>
                  Please note that we may modify or update this Notice from time to time, so please review it
                  periodically. If we make material changes to how we treat Personal Information, we will notify you
                  according to applicable law. Unless otherwise indicated, any changes to this Notice will apply
                  immediately upon posting to our Site. You are responsible for periodically visiting our Site and this
                  Notice to check for any changes.
                </p>
              </div>
            </section>

            {/* Contact Us Section */}
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <User className="w-8 h-8 text-gray-700 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">Contact Us</h2>
                  <p className="text-gray-500 text-sm">How to reach us</p>
                </div>
              </div>
              <div className="text-gray-700 space-y-4">
                <p>
                  If you have any questions about our practices or this Notice, please contact us at{" "}
                  <a href="mailto:legal@siai.live" className="text-blue-600 hover:underline">
                    legal@siai.live
                  </a>
                  .
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  )
}

